import "./index.css"

import Icon from "@utilities/Icon"
import { useId } from "@utilities/useId"
import classNames from "classnames"
import React, { FieldsetHTMLAttributes } from "react"

export type RadioGroupSize = "md" | "sm"

export interface RadioGroupProps {
  /** Optional description text that appears below the label. */
  description?: string
  /** Is invalid. If true, include a description to explain why; and if no description is provided, description defaults to "Please select an option". */
  invalid?: boolean
  /** The label text (required). Renders as `<legend>` (not `<label>`) because `RadioGroup` is a `<fieldset>`. */
  label: string
  /** The size. Does not affect child radios. */
  size?: RadioGroupSize
}

export type RadioGroupElementProps = Omit<
  FieldsetHTMLAttributes<HTMLFieldSetElement>,
  keyof RadioGroupProps
> &
  RadioGroupProps

/**
 * A radio group component that renders as a fieldset with legend.
 * Use to group two or more Radio components.
 * Supports label and description text, sizes, invalid, and disabled state.
 * Label is required and must be a string. Renders as <legend>.
 *
 * @component
 */
export const RadioGroup = ({
  children,
  className,
  description,
  invalid,
  label,
  size = "md",
  ...restProps
}: RadioGroupElementProps) => {
  const combinedClassName = classNames(
    "tds-radio-group",
    size && size === "sm" && "tds-radio-group--sm",
    className
  )
  const stableId = useId()

  const displayDescription =
    description || (invalid ? "Please select an option" : undefined)
  const descriptionId = `tds-radio-group-${stableId}-description`

  return (
    <fieldset
      {...restProps}
      aria-describedby={displayDescription ? descriptionId : undefined}
      aria-invalid={invalid ? "true" : undefined}
      className={combinedClassName}
    >
      <legend>{label}</legend>
      {displayDescription && (
        <p id={descriptionId} className="tds-radio-group-description">
          <Icon
            className="tds-radio-group-description-invalid-icon"
            symbol="general#exclamation-triangle"
            aria-hidden
          />
          {displayDescription}
        </p>
      )}
      <div className="tds-radio-group-fields">{children}</div>
    </fieldset>
  )
}

RadioGroup.displayName = "RadioGroup"
